import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Trash2, Plus, Edit2, GripVertical } from 'lucide-react';

interface DiscardReason {
  id: string;
  reason: string;
  sort_order: number;
  active: number;
}

export function DiscardReasons() {
  const queryClient = useQueryClient();
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingReason, setEditingReason] = useState<DiscardReason | null>(null);

  const { data: reasons, isLoading } = useQuery({
    queryKey: ['discard-reasons'],
    queryFn: async () => {
      const res = await api.get<DiscardReason[]>('/admin/discard-reasons');
      if (res.success) return res.data;
      throw new Error(res.error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.delete(`/admin/discard-reasons/${id}`);
      if (!res.success) throw new Error(res.error || 'Failed to delete');
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discard-reasons'] });
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Discard Reasons</h1>
          <p className="text-foreground-muted">Manage reasons for discarding tools</p>
        </div>
        <Button onClick={() => setShowAddModal(true)}>
          <Plus className="h-4 w-4" />
          Add Reason
        </Button>
      </div>

      <Card>
        <CardBody>
          {isLoading ? (
            <div className="text-center py-12">
              <div className="spinner h-8 w-8 mx-auto" />
            </div>
          ) : reasons?.length === 0 ? (
            <div className="text-center py-12 text-foreground-muted">
              <Trash2 className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">No discard reasons</p>
              <p className="text-sm">Add reasons to categorize tool discards</p>
            </div>
          ) : (
            <div className="space-y-2">
              {reasons?.map((reason, index) => (
                <div
                  key={reason.id}
                  className="flex items-center gap-3 p-3 bg-surface-hover rounded-lg group"
                >
                  <div className="text-foreground-muted cursor-grab">
                    <GripVertical className="h-4 w-4" />
                  </div>
                  <span className="text-sm text-foreground-muted w-8">{index + 1}.</span>
                  <span className="flex-1 font-medium">{reason.reason}</span>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setEditingReason(reason)}
                      title="Edit"
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        if (confirm('Delete this reason?')) {
                          deleteMutation.mutate(reason.id);
                        }
                      }}
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Add Modal */}
      {showAddModal && (
        <ReasonModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            queryClient.invalidateQueries({ queryKey: ['discard-reasons'] });
          }}
          existingCount={reasons?.length || 0}
        />
      )}

      {/* Edit Modal */}
      {editingReason && (
        <ReasonModal
          reason={editingReason}
          onClose={() => setEditingReason(null)}
          onSuccess={() => {
            setEditingReason(null);
            queryClient.invalidateQueries({ queryKey: ['discard-reasons'] });
          }}
          existingCount={reasons?.length || 0}
        />
      )}
    </div>
  );
}

interface ReasonModalProps {
  reason?: DiscardReason;
  onClose: () => void;
  onSuccess: () => void;
  existingCount: number;
}

function ReasonModal({ reason, onClose, onSuccess, existingCount }: ReasonModalProps) {
  const [formData, setFormData] = useState({
    reason: reason?.reason || '',
    sortOrder: reason?.sort_order ?? existingCount,
  });
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      if (reason) {
        const res = await api.put(`/admin/discard-reasons/${reason.id}`, data);
        if (!res.success) throw new Error(res.error || 'Failed to update');
        return res.data;
      } else {
        const res = await api.post('/admin/discard-reasons', data);
        if (!res.success) throw new Error(res.error || 'Failed to create');
        return res.data;
      }
    },
    onSuccess: () => {
      setError('');
      onSuccess();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to save');
    },
  });

  const handleSubmit = () => {
    if (!formData.reason.trim()) {
      setError('Please enter a reason');
      return;
    }
    mutation.mutate(formData);
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={reason ? 'Edit Reason' : 'Add Discard Reason'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={mutation.isPending}>
            {reason ? 'Save Changes' : 'Add Reason'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <div className="p-3 text-sm text-error bg-error/10 rounded-lg">{error}</div>}

        <Input
          label="Reason *"
          value={formData.reason}
          onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
          placeholder="e.g., Worn out, Broken, Consumed"
        />

        <Input
          label="Sort Order"
          type="number"
          min="0"
          value={formData.sortOrder.toString()}
          onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
        />
      </div>
    </Modal>
  );
}
